import axios from "axios";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ANON_STORAGE_KEY = "dhruva_anon_id";
function readAnonId() {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(ANON_STORAGE_KEY) : null;
  } catch (_) { return null; }
}

const client = axios.create({ baseURL: API });

client.interceptors.request.use(async (config) => {
  // Retry path (from the 401 response interceptor) — a fresh token was
  // already stapled onto config.headers.Authorization. Do NOT re-fetch
  // supabase.auth.getSession() here: Supabase's in-memory session may not
  // yet reflect the just-refreshed access_token, and re-fetching would
  // overwrite our fresh header with the stale one, causing the retry to
  // 401 again forever. Skip the getSession call for retries.
  if (config.__retried401) {
    const anon = readAnonId();
    if (anon) {
      config.headers = config.headers || {};
      config.headers["X-Anon-Id"] = anon;
    }
    return config;
  }
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach the anon_id so the backend can attribute this user to a marketing
  // source on signup (see server.get_or_create_user + admin_module.compute_first_referrer_source).
  const anon = readAnonId();
  if (anon) {
    config.headers = config.headers || {};
    config.headers["X-Anon-Id"] = anon;
  }
  return config;
});

/**
 * 401 auto-recovery (Feb 2026, revised Jul 2026):
 *
 * Original behaviour: on ANY 401 we called supabase.auth.signOut() and
 * bounced the user to the landing page. This turned every transient auth
 * blip into a forced logout — including the ~2-3 second cold-start window
 * right after signup where Supabase's admin verify call can time out under
 * load, and the parallel burst of dashboard requests that follows a fresh
 * signup (any one of them getting a 401 nuked the whole session).
 *
 * Users on production reported being "randomly logged out and sent back
 * to sign in" after successful signup — this was that bug.
 *
 * New behaviour uses a SINGLE-FLIGHT refresh pattern:
 *   1. Skip sign-out entirely during the pending-signup-OTP window.
 *      The user is mid-flow; their session is genuine and the OTP step
 *      will settle state normally.
 *   2. The FIRST 401 kicks off a supabase.auth.refreshSession() and stores
 *      the resulting Promise in `refreshPromise`. Every other 401 that
 *      arrives while that refresh is in flight AWAITS the same promise,
 *      then each retries its own request with the fresh access token.
 *      This is essential — a dashboard fires 5-10 parallel protected
 *      requests, and prior versions retried only the first while dropping
 *      the rest (which then painted dashboard-load-error).
 *   3. If refresh resolves with a fresh token → every waiting request
 *      retries transparently with the new Authorization header.
 *   4. If refresh fails (both access AND refresh tokens dead) → we sign
 *      out and redirect to /?session_expired=1, ONCE, from whichever
 *      request got there first.
 *
 * A per-request __retried401 guard prevents an infinite refresh↔401 loop
 * if the backend keeps 401-ing even with a fresh token.
 */
let refreshPromise = null;       // in-flight Promise<string|null> — resolves with fresh access_token
let dyingRedirected = false;     // one-shot flag for the sign-out+redirect path

async function _getFreshToken() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const { data, error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr) return null;
      return data?.session?.access_token || null;
    } catch (_) {
      return null;
    } finally {
      // Clear the guard AFTER the microtask so late-arriving 401s within
      // the same tick still coalesce onto this refresh.
      setTimeout(() => { refreshPromise = null; }, 0);
    }
  })();
  return refreshPromise;
}

client.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const status = error?.response?.status;
    if (status !== 401) return Promise.reject(error);

    const cfg = error.config || {};
    // Per-request retry guard — prevents infinite refresh↔401 loop if the
    // backend rejects the freshly-refreshed token too.
    if (cfg.__retried401) return Promise.reject(error);

    // (1) Fresh signup escape hatch — do not sign the user out while their
    // signup OTP flow is still in progress. A 401 here is almost always a
    // race between the AuthModal's parallel API calls and the backend's
    // user-row creation.
    try {
      if (typeof window !== "undefined" &&
          sessionStorage.getItem("dhruva_pending_signup_otp") === "1") {
        return Promise.reject(error);
      }
    } catch (_) { /* sessionStorage blocked */ }

    // (2) Single-flight refresh. Every concurrent 401 awaits the same
    // in-flight refresh promise, then retries with the fresh token.
    const freshToken = await _getFreshToken();
    if (freshToken) {
      cfg.__retried401 = true;
      cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${freshToken}` };
      return client.request(cfg);
    }

    // (3) Genuine dead session — clear it and bounce (exactly once, no
    // matter how many parallel 401s reached us).
    if (!dyingRedirected) {
      dyingRedirected = true;
      try { await supabase.auth.signOut(); } catch (_) { /* ignore */ }
      const path = typeof window !== "undefined" ? window.location.pathname : "/";
      // Feb 2026 (landing revamp): /top-colleges, /cap-rounds and
      // /engineering-branches were made public so the top-nav Streams
      // dropdown can send anonymous visitors here. If a stray 401 comes
      // back from any incidental API call on these pages, don't bounce
      // the user to the landing — just let the interceptor swallow it.
      const onPublicPage = ["/", "/sign-in", "/terms", "/privacy", "/top-colleges", "/cap-rounds", "/engineering-branches", "/guides/neet", "/neet/instant"].includes(path);
      if (!onPublicPage && typeof window !== "undefined") {
        try { sessionStorage.setItem("post_signin_redirect", path); } catch (_) { /* ignore */ }
        window.location.assign("/?session_expired=1");
      }
      // Reset guard well after the navigation actually happens (or never
      // does, on public pages).
      setTimeout(() => { dyingRedirected = false; }, 3000);
    }
    return Promise.reject(error);
  },
);

export default client;

// Public meta call (no auth)
export const fetchMeta = async () => {
  const r = await axios.get(`${API}/meta/options`);
  return r.data;
};
